import {
  IndexTable,
  LegacyCard,
  IndexFilters,
  useSetIndexFiltersMode,
  useIndexResourceState,
  Text,
  IndexFiltersMode,
  useBreakpoints,
  EmptySearchResult,
  Button,
  Icon,
  InlineStack,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useState, useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useMemo } from "react";


export default function Customers() {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const [orders, setOrders] = useState([]);  // Initialize as an empty array
  const [itemStrings, setItemStrings] = useState([]);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
  const [queryValue, setQueryValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1); // Current page
  const [totalPages, setTotalPages] = useState(1); // Total pages

  const [adminCheck, setAdminCheck] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [customerDeleteId, setCustomerDeleteId] = useState(null);

  const onCreateNewView = async (value) => {
    await sleep(500);
    setItemStrings([...itemStrings, value]);
    return true;
  };

  const onHandleCancel = () => { };

  const tabs = itemStrings.map((item, index) => ({
    content: item,
    index,
    onAction: () => { },
    id: `${item}-${index}`,
    isLocked: index === 0,
    actions:
      index === 0
        ? []
        : [
        ],
  }));


  const handleQueryValueChange = useCallback(
    (value) => {
      setQueryValue(value);
    },
    []
  );

  const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);

  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
  }, [
    handleQueryValueRemove,
  ]);

  const filters = [];
  const appliedFilters = [];

  function debounce(func, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }

  const fetchCustomers = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    if (!token) return;

    const decoded = jwtDecode(token);
    const RepsId = decoded.ids;
    const checkRole = decoded.role;
    setAdminCheck(checkRole);
    const queryParam = checkRole === 'sales' ? `repsid=${RepsId}` : '';
    setIsLoading(true);

    try {
      const url = queryValue
        ? `${baseUrl}/api/customers?search=${encodeURIComponent(queryValue)}&page=${page}&limit=10&${queryParam}`
        : `${baseUrl}/api/customers?page=${page}&limit=10&${queryParam}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.customers && Array.isArray(data.customers)) {
        setOrders(data.customers);
        setTotalPages(data.totalPages);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error.message);
    } finally {
      setIsLoading(false);
    }
  }, [queryValue, page]);

  const debouncedFetch = useMemo(() => debounce(fetchCustomers, 400), [fetchCustomers]);

  useEffect(() => {
    debouncedFetch();
  }, [debouncedFetch]);


  const resourceName = {
    singular: 'order',
    plural: 'orders',
  };

  const { selectedResources } =
    useIndexResourceState(orders);

  const emptyStateMarkup = (
    <EmptySearchResult
      title={'No customers found'}
      description={'Try changing the filters or search term'}
      withIllustration
    />
  );

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const dateObj = new Date(isoDate);
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const rowMarkup = orders && orders.length > 0
    ? orders.map(
      (
        { customerId, _id, json, createdAt },
        index
      ) => (
        <IndexTable.Row
          id={customerId}
          key={customerId}
          selected={selectedResources.includes(customerId)}
          position={index}
        >
          <IndexTable.Cell>
            <NavLink to={_id} state={{ customer: orders[index], viewMode: true }}>
              <Text variant="bodyMd" fontWeight="bold" as="span">
                {customerId}
              </Text>
            </NavLink>
          </IndexTable.Cell>
          <IndexTable.Cell>{json?.name}</IndexTable.Cell>
          <IndexTable.Cell>{json?.gender}</IndexTable.Cell>
          <IndexTable.Cell>
            <NavLink to={`orders/${customerId}`} state={{ customer: orders[index] }}>Orders</NavLink>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <>
              <div className='cutomer_update_btn'>
                <NavLink to="/dashboard/orders/create" state={{ customer: orders[index] }}>Create Order</NavLink>
                <NavLink to={_id} state={{ customer: orders[index] }}>
                  Edit Customer
                </NavLink>
                <NavLink to={_id} state={{ customer: orders[index], viewMode: true }}>
                  View
                </NavLink>
                {adminCheck === "admin" && (
                  <span
                    onClick={() => handleCustomerDelete(_id)}
                    style={{ cursor: "pointer" }}
                  >
                    <Icon source={DeleteIcon} tone="base" />
                  </span>
                )}
              </div>
            </>
          </IndexTable.Cell>
          <IndexTable.Cell>{json?.customerNotes}</IndexTable.Cell>
          <IndexTable.Cell>{formatDate(createdAt)}</IndexTable.Cell>
        </IndexTable.Row>
      )
    )
    : (
      <IndexTable.Row>
        <IndexTable.Cell colSpan={7}>No customers found</IndexTable.Cell>
      </IndexTable.Row>
    );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleCustomerDelete = (id) => {
    setCustomerDeleteId(id);
    setShowPopup(true);
  };

  const confirmDelete = async () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    if (!token || !customerDeleteId) return;

    try {
      const response = await fetch(`${baseUrl}/api/customers/${customerDeleteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setOrders((prev) => prev.filter((c) => c._id !== customerDeleteId));
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setShowPopup(false);
      setCustomerDeleteId(null);
    }
  };

  return (
    <>
      <div className='customerList_section'>
        <div className='button_option'>
          <h2>All Customers</h2>
          <NavLink to="add">
            <Button variant="primary">Add Customer</Button>
          </NavLink>
        </div>

        <LegacyCard>
          <IndexFilters
            loading={isLoading}
            queryValue={queryValue}
            queryPlaceholder="Search customers"
            onQueryChange={handleQueryValueChange}
            onQueryClear={handleQueryValueRemove}
            cancelAction={{
              onAction: onHandleCancel,
              disabled: false,
              loading: false,
            }}
            tabs={tabs}
            canCreateNewView
            onCreateNewView={onCreateNewView}
            filters={filters}
            appliedFilters={appliedFilters}
            onClearAll={handleFiltersClearAll}
            mode={mode}
            setMode={setMode} />

          <IndexTable
            condensed={useBreakpoints().smDown}
            resourceName={resourceName}
            emptyState={emptyStateMarkup}
            itemCount={orders.length}
            headings={[
              { title: 'Customer ID' },
              { title: 'Customer Name' },
              { title: 'Gender' },
              { title: 'Orders' },
              { title: 'Action' },
              { title: 'Notes' },
              { title: 'Created Date' },
            ]}
            selectable={false}
            pagination={{
              hasNext: page < totalPages,
              onNext: () => handlePageChange(page + 1),
              hasPrevious: page > 1,
              onPrevious: () => handlePageChange(page - 1),
              currentPage: page,
              totalPages,
            }}
          >
            {rowMarkup}
          </IndexTable>
        </LegacyCard>
      </div>
      {showPopup && (
        <div className="popup_delete">
          <div className="popup_delete_box">
            <h3>Are you sure you want to delete this customer?</h3>
            <InlineStack gap="300">
              <Button variant="primary" onClick={confirmDelete}>Yes</Button>
              <Button variant="primary" onClick={() => setShowPopup(false)}>No</Button>
            </InlineStack>
          </div>
        </div>
      )}
    </>
  );
}